export const WISDOM_CHAIN_ID = 2307;
export const WISDOM_CHAIN_ID_HEX = "0x903";

// A contract address that is still a placeholder / not a real 20-byte address.
// The marketplace/data stubs ship with 0xYourContractAddress etc., and must
// never trigger a transaction — on Ethereum mainnet that would prompt a real
// ETH send to a garbage address (REACH-1337).
export function isPlaceholderAddress(address) {
    return !address || /^0xyour/i.test(address) || !/^0x[0-9a-fA-F]{40}$/.test(address);
}

// Guard every on-chain write: refuse placeholder contracts, then force the
// wallet onto the Wisdom clique chain (0x903) before any tx is built.
export async function ensureWisdomChainForContract(address) {
    if (isPlaceholderAddress(address)) {
        throw new Error("This on-chain feature is not available yet — no contract is deployed.");
    }
    await ensureWisdomChain();
}

export function wisdomRpcUrl() {
    if (typeof window !== "undefined" && window.location && window.location.origin) {
        return window.location.origin + "/blockchain/";
    }
    return "https://wisdomacademy.ai/blockchain/";
}

export function wisdomChainParams() {
    return {
        chainId: WISDOM_CHAIN_ID_HEX,
        chainName: "Wisdom Academy",
        nativeCurrency: { name: "WSD", symbol: "WSD", decimals: 18 },
        rpcUrls: [wisdomRpcUrl()],
    };
}

export async function ensureWisdomChain() {
    if (!window.ethereum) {
        throw new Error("MetaMask not detected. Please install MetaMask.");
    }
    try {
        await window.ethereum.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: WISDOM_CHAIN_ID_HEX }],
        });
    } catch (err) {
        const needAdd = err && (err.code === 4902 || String(err.message || "").includes("Unrecognized chain"));
        if (!needAdd) {
            throw err;
        }
        await window.ethereum.request({
            method: "wallet_addEthereumChain",
            params: [wisdomChainParams()],
        });
    }
}

export async function connectWisdomWallet() {
    if (!window.ethereum) {
        throw new Error("MetaMask not detected. Please install MetaMask.");
    }
    // Request the account FIRST so login never depends on the chain being
    // reachable. Switching/adding the Wisdom network is best-effort: if the
    // /blockchain/ RPC is down (e.g. the node is still starting), the user can
    // still connect and sign in; on-chain features just degrade until it's up.
    const accounts = await window.ethereum.request({ method: "eth_requestAccounts" });
    if (!accounts || !accounts.length) {
        throw new Error("No wallet account returned.");
    }
    try {
        await ensureWisdomChain();
    } catch (err) {
        console.warn("Could not switch to the Wisdom network automatically:", err);
    }
    return accounts[0];
}

export async function silentWisdomAccount() {
    if (!window.ethereum) {
        return null;
    }
    try {
        const accounts = await window.ethereum.request({ method: "eth_accounts" });
        if (!accounts || !accounts.length) {
            return null;
        }
        return accounts[0];
    } catch (e) {
        return null;
    }
}
